import React, { memo, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router';
import { Link, useHistory } from 'react-router-dom';

import { Box, Typography } from '@mui/material';
import { MoreIcon } from '@sharedCR/coreDesignSystem/icons';
import BadgeCDS from 'app/shared/componentsRestyled/coreDesignSystem/BadgeCDS';
import PropTypes from 'prop-types';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { selectNavigationConfig } from 'store/global/selectors/navigation.selectors';
import { selectPushNotificationUnreadCount } from 'store/pushNotifications/selectors';
import { fetchChannelsAction } from 'store/teamChat/actions';
import { selectTeamChatTotalUnreadCount } from 'store/teamChat/selectors';

import MobileTabsContext from '../MobileTabsProvider/MobileTabsContext';
import { TAB_ID } from '../MobileTabsProvider/MobileTabsProvider.constants';

import {
  BOTTOM_NAV_ICON_SIZE,
  BOTTOM_NAV_ICONS,
  CREATE_ITEM_ID,
  MORE_ITEM_ID,
  NAV_ACCENT_COLOR,
  NOTIFICATIONS_ITEM_ID,
  SEARCH_ITEM_ID,
} from './BottomNav.constants';
import { NAV_MEMBER_KIND } from './BottomNav.groups.constants';
import {
  barStyles,
  getBarVisibilityStyles,
  getTabButtonStyles,
  getTabIconWrapperStyles,
  groupCueDotActiveStyles,
  groupCueDotStyles,
  heldTabIconWrapperStyles,
  tabBadgeAnchorStyles,
  tabBadgeStyles,
  tabLabelStyles,
} from './BottomNav.styles';
import { computeVisibleGroups, getMemberTabId } from './BottomNav.utils';
import CommandPalette from './components/CommandPalette';
import CreateSheet from './components/CreateSheet';
import MoreSheet from './components/MoreSheet';
import NavGroupCard from './components/NavGroupCard';
import NotificationsSheet from './components/NotificationsSheet';
import useCreatableObjects from './hooks/useCreatableObjects';
import { useHideOnScroll } from './hooks/useHideOnScroll';
import useLongPress from './hooks/useLongPress';
import useMessagesTabEnabled from './hooks/useMessagesTabEnabled';
import useNotificationsTabEnabled from './hooks/useNotificationsTabEnabled';
import usePhoneTabEnabled from './hooks/usePhoneTabEnabled';

/** Maximum unread count shown in the badge before capping with a '+'. */
const BADGE_MAX = 99;

/** Height (px) of the glass bar — used for card positioning. */
const NAV_BAR_HEIGHT = 70;

/** Gap (px) between the card bottom and the bar top. */
const NAV_CARD_GAP = 8;

/** Minimum horizontal margin (px) from the viewport edges for the card. */
const NAV_CARD_EDGE_MARGIN = 12;

/** Minimum card width (px). */
const NAV_CARD_MIN_WIDTH = 208;

const navGroupShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  isGroup: PropTypes.bool.isRequired,
  members: PropTypes.array.isRequired,
  primary: PropTypes.object,
  badgeCount: PropTypes.number,
});

/** Sx array for a held group tab: lifts + gradient fill (no isActive glow). */
const heldTabButtonSx = [
  getTabButtonStyles({ isActive: false }),
  { transform: 'translateY(-3px)' },
];

/**
 * Single tab in the bottom navigation bar.
 * Supports both plain tabs (short-press only) and expandable groups
 * (short press = primary; long press = speed-dial card).
 *
 * @param {Object} props
 * @param {number} [props.badgeCount] - unread count on the tab icon (0 = hidden)
 * @param {Object} props.group - NavGroup descriptor
 * @param {boolean} props.isActive - whether this tab / any of its members is currently active
 * @param {boolean} props.isHeld - whether this tab is being long-pressed (glow ring)
 * @param {Function} props.onLongPress - called on a long press (only for isGroup tabs)
 * @param {Function} props.onTap - called on a short press
 * @param {Function} [props.tabRef] - ref callback for measuring the tab's position
 */
const BottomNavGroupTab = memo(
  ({ badgeCount = 0, group, isActive, isHeld, onLongPress, onTap, tabRef }) => {
    const isMoreTab = group.id === MORE_ITEM_ID;
    const { label } = group;

    const onTapGroup = useCallback(() => onTap(group), [group, onTap]);
    const onLongPressGroup = useCallback(() => onLongPress(group), [group, onLongPress]);

    // Single-route tabs render as a real <Link> (href, keyboard, modifier-click open-in-new).
    // A plain left-click is routed through the same `onTabTap` path as the button tabs so it
    // restores the tab's remembered location + scroll instead of navigating to the raw root URL.
    const onRouteTabClick = useCallback(
      event => {
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }

        event.preventDefault();
        onTapGroup();
      },
      [onTapGroup],
    );

    const longPress = useLongPress({
      onTap: onTapGroup,
      onLongPress: onLongPressGroup,
    });

    const primaryIcon = group.primary ? group.primary.icon : null;

    const rawIconContent = useMemo(() => {
      if (isMoreTab) {
        return <MoreIcon size={BOTTOM_NAV_ICON_SIZE} />;
      }

      const CdsIcon = BOTTOM_NAV_ICONS[group.id] ?? primaryIcon;

      if (CdsIcon) {
        return <CdsIcon size={BOTTOM_NAV_ICON_SIZE} />;
      }

      return null;
    }, [isMoreTab, group.id, primaryIcon]);

    const cappedBadge = Math.min(badgeCount, BADGE_MAX);
    const badgeValue = cappedBadge === BADGE_MAX ? `${BADGE_MAX}+` : String(cappedBadge);

    const iconContent =
      badgeCount > 0 ? (
        <Box sx={tabBadgeAnchorStyles}>
          {rawIconContent}
          <Box sx={tabBadgeStyles}>
            <BadgeCDS value={badgeValue} />
          </Box>
        </Box>
      ) : (
        rawIconContent
      );

    const iconWrapperSx = useMemo(() => {
      if (isHeld) {
        return heldTabIconWrapperStyles;
      }

      if (group.isGroup) {
        return [
          getTabIconWrapperStyles({ isActive }),
          groupCueDotStyles,
          isActive ? groupCueDotActiveStyles : null,
        ];
      }

      return getTabIconWrapperStyles({ isActive });
    }, [isHeld, isActive, group.isGroup]);

    const content = (
      <>
        <Box sx={iconWrapperSx}>{iconContent}</Box>
        <Typography
          color={isActive ? NAV_ACCENT_COLOR : 'text.secondary'}
          sx={tabLabelStyles}
          variant={isActive ? 'medium12' : 'regular12'}
        >
          {label}
        </Typography>
      </>
    );

    // More tab: plain button — opens MoreSheet on tap, no long-press
    if (isMoreTab) {
      return (
        <Box
          aria-expanded={isActive}
          component="button"
          data-testid={`bottom-nav-tab-${group.id}`}
          ref={tabRef}
          sx={getTabButtonStyles({ isActive })}
          onClick={onTapGroup}
        >
          {content}
        </Box>
      );
    }

    // Single-member ROUTE tab: render as Link for native navigation semantics
    const { primary } = group;
    const isRouteTab = !group.isGroup && primary && primary.kind === NAV_MEMBER_KIND.ROUTE;

    if (isRouteTab && primary.url) {
      return (
        <Box
          aria-current={isActive ? 'page' : undefined}
          component={Link}
          data-testid={`bottom-nav-tab-${group.id}`}
          ref={tabRef}
          sx={getTabButtonStyles({ isActive })}
          to={primary.url}
          onClick={onRouteTabClick}
        >
          {content}
        </Box>
      );
    }

    // Group tab or single action tab: pointer-event-driven button with long-press
    return (
      <Box
        aria-expanded={group.isGroup ? isHeld : isActive}
        component="button"
        data-testid={`bottom-nav-tab-${group.id}`}
        ref={tabRef}
        sx={isHeld ? heldTabButtonSx : getTabButtonStyles({ isActive })}
        {...longPress}
      >
        {content}
      </Box>
    );
  },
);

BottomNavGroupTab.propTypes = {
  group: navGroupShape.isRequired,
  isActive: PropTypes.bool.isRequired,
  isHeld: PropTypes.bool.isRequired,
  onLongPress: PropTypes.func.isRequired,
  onTap: PropTypes.func.isRequired,
  badgeCount: PropTypes.number,
  tabRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

/**
 * Glass bottom navigation bar for mobile / native.
 *
 * Reads the permission-filtered nav config from Redux and composes it into
 * expandable groups. Bar order: Pipelines · Home · Comms · Pricing · More.
 *
 * - Single-member groups render as plain tabs (no dot-cue, no hold behavior).
 * - Multi-member groups show a two-dot cue; tap = primary, long-press = card.
 */
const BottomNav = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const mobileTabs = useContext(MobileTabsContext);

  const activeTabId = mobileTabs?.activeTabId ?? null;

  const navItems = useSelector(selectNavigationConfig);
  const notificationsUnreadCount = useSelector(selectPushNotificationUnreadCount);
  const messagesUnreadCount = useSelector(selectTeamChatTotalUnreadCount);

  const showNotifications = useNotificationsTabEnabled();
  const showMessages = useMessagesTabEnabled();
  const showPhone = usePhoneTabEnabled();

  const { canCreateLoan, canCreateLead, canCreateContact } = useCreatableObjects();
  const showCreate = canCreateLoan || canCreateLead || canCreateContact;

  const isHidden = useHideOnScroll(location.pathname);

  const barSx = useMemo(() => [barStyles, getBarVisibilityStyles({ isHidden })], [isHidden]);

  const [isMoreSheetOpen, setIsMoreSheetOpen] = useState(false);
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [openCardGroupId, setOpenCardGroupId] = useState(null);

  // Ref map from group.id → tab DOM element (for card positioning)
  const tabRefMap = useRef({});

  // Stable ref-setter callbacks keyed by group id — avoids creating a new function on every render.
  const tabRefCallbacks = useRef({});

  const getTabRefCallback = useCallback(groupId => {
    if (!tabRefCallbacks.current[groupId]) {
      tabRefCallbacks.current[groupId] = el => {
        tabRefMap.current[groupId] = el;
      };
    }

    return tabRefCallbacks.current[groupId];
  }, []);

  const { groups, overflowItems } = useMemo(
    () =>
      computeVisibleGroups(navItems, {
        showMessages,
        showNotifications,
        showPhone,
        showCreate,
        messagesUnreadCount: messagesUnreadCount ?? 0,
        notificationsUnreadCount: notificationsUnreadCount ?? 0,
      }),
    [
      navItems,
      showMessages,
      showNotifications,
      showPhone,
      showCreate,
      messagesUnreadCount,
      notificationsUnreadCount,
    ],
  );

  const openCard = useCallback(groupId => {
    setOpenCardGroupId(groupId);
  }, []);

  const closeCard = useCallback(() => {
    setOpenCardGroupId(null);
  }, []);

  const onCloseMoreSheet = useCallback(() => {
    setIsMoreSheetOpen(false);
  }, []);

  const onClosePalette = useCallback(() => {
    setIsPaletteOpen(false);
  }, []);

  const onCloseNotifications = useCallback(() => {
    setIsNotificationsOpen(false);
  }, []);

  const onCloseCreateSheet = useCallback(() => {
    setIsCreateSheetOpen(false);
  }, []);

  const dispatchAction = useCallback(actionId => {
    if (actionId === MORE_ITEM_ID) {
      setIsMoreSheetOpen(true);

      return;
    }

    if (actionId === SEARCH_ITEM_ID) {
      setIsPaletteOpen(true);

      return;
    }

    if (actionId === NOTIFICATIONS_ITEM_ID) {
      setIsNotificationsOpen(true);

      return;
    }

    if (actionId === CREATE_ITEM_ID) {
      setIsCreateSheetOpen(true);

      return;
    }
  }, []);

  /**
   * Short press on a group tab:
   * - If a card is already open, close it first.
   * - More tab → open MoreSheet.
   * - Multi-member group → navigate/action the primary member.
   * - Single-member → navigate/action directly.
   */
  const onTabTap = useCallback(
    group => {
      if (group.id === MORE_ITEM_ID) {
        closeCard();
        setIsMoreSheetOpen(true);

        return;
      }

      // Peer groups (Comms) open the selection card on tap (toggle) — no single primary to fire.
      if (group.tapOpensCard && group.isGroup) {
        setOpenCardGroupId(prev => (prev === group.id ? null : group.id));

        return;
      }

      closeCard();

      const { primary } = group;

      if (!primary) {
        return;
      }

      if (primary.kind === NAV_MEMBER_KIND.ROUTE && primary.url) {
        const tabId = getMemberTabId(primary, group.id);

        mobileTabs?.setPendingTabId(tabId);
        const remembered = mobileTabs?.getTabLocation(tabId);
        history.push(remembered ?? primary.url);

        return;
      }

      if (primary.kind === NAV_MEMBER_KIND.ACTION) {
        dispatchAction(primary.actionId);
      }
    },
    [closeCard, dispatchAction, history, mobileTabs],
  );

  /**
   * Long press on a multi-member group tab → open the speed-dial card.
   * Single-member tabs never produce a long-press (useLongPress fires onTap instead
   * since the timer fires but openCard is not called for isGroup=false groups).
   */
  const onTabLongPress = useCallback(
    group => {
      if (!group.isGroup) {
        return;
      }

      openCard(group.id);
    },
    [openCard],
  );

  /**
   * A row inside the speed-dial card was tapped.
   * Close the card, then navigate or dispatch the action.
   */
  const onCardMemberSelect = useCallback(
    member => {
      closeCard();

      if (member.kind === NAV_MEMBER_KIND.ROUTE && member.url) {
        mobileTabs?.setPendingTabId(getMemberTabId(member, openCardGroupId));
        history.push(member.url);

        return;
      }

      if (member.kind === NAV_MEMBER_KIND.ACTION) {
        dispatchAction(member.actionId);
      }
    },
    [closeCard, dispatchAction, history, mobileTabs, openCardGroupId],
  );

  useEffectSafeStrictMode(() => {
    if (showMessages) {
      dispatch(fetchChannelsAction());
    }
  }, [dispatch, showMessages]);

  // Compute card positioning based on the held tab's DOM rect
  const cardPosition = useMemo(() => {
    if (!openCardGroupId) {
      return null;
    }

    const tabEl = tabRefMap.current[openCardGroupId];

    if (!tabEl) {
      return null;
    }

    const tabRect = tabEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const tabCenterX = tabRect.left + tabRect.width / 2;
    const rawLeft = tabCenterX - NAV_CARD_MIN_WIDTH / 2;
    const anchorLeft = Math.max(
      NAV_CARD_EDGE_MARGIN,
      Math.min(rawLeft, viewportWidth - NAV_CARD_EDGE_MARGIN - NAV_CARD_MIN_WIDTH),
    );
    const tailOffset = tabCenterX - anchorLeft;
    const bottomOffset = NAV_BAR_HEIGHT + NAV_CARD_GAP;

    return { anchorLeft, tailOffset, bottomOffset };
  }, [openCardGroupId]);

  const openGroup = groups.find(g => g.id === openCardGroupId) ?? null;

  return (
    <>
      <Box aria-label="Main navigation" component="nav" sx={barSx}>
        {groups.map(group => {
          const isActive = resolveGroupActive(group, location.pathname, activeTabId);
          const isHeld = group.id === openCardGroupId;

          return (
            <BottomNavGroupTab
              badgeCount={group.badgeCount ?? 0}
              group={group}
              isActive={isActive}
              isHeld={isHeld}
              key={group.id}
              tabRef={getTabRefCallback(group.id)}
              onLongPress={onTabLongPress}
              onTap={onTabTap}
            />
          );
        })}
      </Box>

      {openGroup && cardPosition && (
        <NavGroupCard
          activeUrl={location.pathname}
          anchorLeft={cardPosition.anchorLeft}
          bottomOffset={cardPosition.bottomOffset}
          groupLabel={openGroup.label}
          members={openGroup.members}
          tailOffset={cardPosition.tailOffset}
          onScrimClick={closeCard}
          onSelect={onCardMemberSelect}
        />
      )}

      <MoreSheet isOpen={isMoreSheetOpen} items={overflowItems} onClose={onCloseMoreSheet} />

      <CommandPalette isOpen={isPaletteOpen} navItems={navItems} onClose={onClosePalette} />

      <NotificationsSheet isOpen={isNotificationsOpen} onClose={onCloseNotifications} />

      <CreateSheet
        canCreateContact={canCreateContact}
        canCreateLead={canCreateLead}
        canCreateLoan={canCreateLoan}
        isOpen={isCreateSheetOpen}
        onClose={onCloseCreateSheet}
      />
    </>
  );
};

/**
 * Determines whether a group's tab should render as "active".
 * - A ROUTE member's tab is active when its URL matches the current path.
 * - More tab is never active by route.
 * - For groups: active if any member's URL matches.
 *
 * @param {Object} group - NavGroup descriptor
 * @param {string} pathname - current route pathname
 * @param {string|null} activeTabId - the active keep-alive tab (highlights the tab whose stack is
 *   showing even when the current route — e.g. a loan editor — is not itself a bar destination)
 * @returns {boolean}
 */
const resolveGroupActive = (group, pathname, activeTabId) => {
  if (group.id === MORE_ITEM_ID) {
    return false;
  }

  const matchesRoute = group.members.some(
    m =>
      m.kind === NAV_MEMBER_KIND.ROUTE &&
      m.url &&
      !!matchPath(pathname, { path: m.url, end: false }),
  );

  if (matchesRoute || group.id === activeTabId) {
    return true;
  }

  return group.id === TAB_ID.COMMS && activeTabId === TAB_ID.PHONE;
};

export default memo(BottomNav);
